import React from 'react';

import {buttonSecondary} from '../lib/buttonStyles.js';

// ============================================================================
// Toast — one-off success messages (Design Constitution §4 Feedback: F01, F10, Q40)
// ----------------------------------------------------------------------------
// One sentence, bottom-center on phones and bottom-right on desktop, gone after
// 4s; with an Undo (after a soft delete or send) it stays 8s. One toast at a
// time: a new one replaces the old. Errors never use a toast; they are a danger
// banner (F04). A lasting good state is an ok Notice, not a toast (Q40).
//   const toast = useToast();
//   toast.show({message: 'Feed costs saved.'});
//   toast.show({message: 'Report deleted.', undo: restore});
// Styling lives in index.html (.wcf-toast): a white card with the hover shadow
// on the confirms-and-toasts layer (z 500), above the phone bottom bar.
// ============================================================================

export type ToastInput = {
  message: string;
  undo?: () => void;
  undoLabel?: string;
  durationMs?: number;
};

type ToastApi = {show: (toast: ToastInput) => void};

const ToastContext = React.createContext<ToastApi | null>(null);

// Outside a provider (an isolated component test) show() does nothing rather
// than throwing; every real surface renders under AppProviders.
const NO_TOAST: ToastApi = {show: () => {}};

export function useToast(): ToastApi {
  return React.useContext(ToastContext) ?? NO_TOAST;
}

type ActiveToast = ToastInput & {id: number};

export function ToastProvider({children}: {children?: React.ReactNode}) {
  const [toast, setToast] = React.useState<ActiveToast | null>(null);
  const timer = React.useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const seq = React.useRef(0);

  const show = React.useCallback((next: ToastInput) => {
    clearTimeout(timer.current);
    const id = ++seq.current;
    setToast({...next, id});
    const ms = next.durationMs ?? (next.undo ? 8000 : 4000);
    timer.current = setTimeout(() => setToast((cur) => (cur && cur.id === id ? null : cur)), ms);
  }, []);

  React.useEffect(() => () => clearTimeout(timer.current), []);
  const api = React.useMemo(() => ({show}), [show]);

  function undo() {
    if (!toast?.undo) return;
    clearTimeout(timer.current);
    const run = toast.undo;
    setToast(null);
    run();
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toast && (
        <div className="wcf-toast" role="status" aria-live="polite" data-toast key={toast.id}>
          <span className="wcf-toast__message">{toast.message}</span>
          {toast.undo && (
            <button type="button" onClick={undo} style={buttonSecondary} data-toast-undo>
              {toast.undoLabel || 'Undo'}
            </button>
          )}
        </div>
      )}
    </ToastContext.Provider>
  );
}
